import PropTypes from 'prop-types';
import { Controller, useFieldArray, useWatch } from 'react-hook-form';

import { cn } from '#/lib/utils';
import { Button } from '#/modules/commonComponents/button';
import { Checkbox } from '#/modules/commonComponents/checkbox';
import PlainRegisteredInput from '#/modules/commonComponents/form/PlainRegisteredInput';
import FloatingSelect from '#/modules/commonComponents/form/select/FloatingSelect';
import {
  BOOLEAN_EXPECTED_OPTIONS,
  DECIMAL_OPERATOR_OPTIONS,
  TEXT_OPERATOR_OPTIONS,
} from '#/modules/imsMgmt/settings/inspectionKind/helper/constants';

function inclusiveLabel(side) {
  return side === 'min' ? '含下限' : '含上限';
}

const InspectionCriteriaFields = ({
  index,
  control,
  register,
  errors,
  readOnly = false,
}) => {
  const dataType = useWatch({ control, name: `items.${index}.dataType` });
  const operator = useWatch({ control, name: `items.${index}.operator` });
  const optionRows = useWatch({ control, name: `items.${index}.options` }) ?? [];
  const { fields, append, remove } = useFieldArray({
    control,
    name: `items.${index}.options`,
  });

  if (dataType === 'JUDGMENT') {
    return <p className="text-sm text-muted-foreground">直接判定，沒有其他條件欄位。</p>;
  }

  return (
    <div className="mt-3 gap-3 grid grid-cols-1 md:grid-cols-2">
      {dataType === 'DECIMAL' ? (
        <>
          <FloatingSelect
            id={`items.${index}.operator`}
            label="比較方式"
            options={DECIMAL_OPERATOR_OPTIONS}
            control={control}
            errors={errors}
            isDisabled={readOnly}
          />
          <PlainRegisteredInput
            id={`items.${index}.scale`}
            register={register}
            errors={errors}
            type="number"
            min="0"
            max="12"
            step="1"
            readOnly={readOnly}
            mobileLabel="小數位數"
            ariaLabel="小數位數"
          />
          <PlainRegisteredInput
            id={`items.${index}.unit`}
            register={register}
            errors={errors}
            readOnly={readOnly}
            mobileLabel="單位"
            ariaLabel="單位"
            placeholder="選填"
          />
          {operator === 'BETWEEN' ? (
            <>
              <PlainRegisteredInput
                id={`items.${index}.min`}
                register={register}
                errors={errors}
                readOnly={readOnly}
                mobileLabel="下限"
                ariaLabel="下限"
              />
              <PlainRegisteredInput
                id={`items.${index}.max`}
                register={register}
                errors={errors}
                readOnly={readOnly}
                mobileLabel="上限"
                ariaLabel="上限"
              />
              <InclusiveField
                index={index}
                side="min"
                control={control}
                disabled={readOnly}
              />
              <InclusiveField
                index={index}
                side="max"
                control={control}
                disabled={readOnly}
              />
            </>
          ) : (
            <PlainRegisteredInput
              id={`items.${index}.expectedValue`}
              register={register}
              errors={errors}
              readOnly={readOnly}
              mobileLabel="期望值"
              ariaLabel="期望值"
            />
          )}
        </>
      ) : null}

      {dataType === 'BOOLEAN' ? (
        <FloatingSelect
          id={`items.${index}.expectedValue`}
          label="期望值"
          options={BOOLEAN_EXPECTED_OPTIONS}
          control={control}
          errors={errors}
          isDisabled={readOnly}
        />
      ) : null}

      {dataType === 'TEXT' ? (
        <>
          <FloatingSelect
            id={`items.${index}.operator`}
            label="比對方式"
            options={TEXT_OPERATOR_OPTIONS}
            control={control}
            errors={errors}
            isDisabled={readOnly}
          />
          {operator === 'EXACT_EQUALS' ? (
            <PlainRegisteredInput
              id={`items.${index}.expectedValue`}
              register={register}
              errors={errors}
              readOnly={readOnly}
              mobileLabel="期望文字"
              ariaLabel="期望文字"
            />
          ) : null}
        </>
      ) : null}

      {dataType === 'SINGLE_SELECT' ? (
        <div className="md:col-span-2">
          <div className="mb-2 flex items-center justify-between gap-2">
            <p className="mb-0 text-sm font-medium">選項</p>
            {readOnly ? null : (
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => append({ value: '', label: '' })}
              >
                新增選項
              </Button>
            )}
          </div>
          <ul className="m-0 list-none p-0">
            {fields.map((field, optionIndex) => (
              <li
                key={field.id}
                className={cn(
                  'mb-2 gap-2 grid grid-cols-1',
                  readOnly ? 'md:grid-cols-2' : 'md:grid-cols-[1fr_1fr_auto]',
                )}
              >
                <PlainRegisteredInput
                  id={`items.${index}.options.${optionIndex}.value`}
                  register={register}
                  errors={errors}
                  readOnly={readOnly}
                  mobileLabel="選項代碼"
                  ariaLabel="選項代碼"
                />
                <PlainRegisteredInput
                  id={`items.${index}.options.${optionIndex}.label`}
                  register={register}
                  errors={errors}
                  readOnly={readOnly}
                  mobileLabel="選項名稱"
                  ariaLabel="選項名稱"
                />
                {readOnly ? null : (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={fields.length <= 1}
                    onClick={() => remove(optionIndex)}
                  >
                    移除
                  </Button>
                )}
              </li>
            ))}
          </ul>
          <PassingValues
            index={index}
            control={control}
            optionRows={optionRows}
            errors={errors}
            disabled={readOnly}
          />
        </div>
      ) : null}
    </div>
  );
};

function InclusiveField({ index, side, control, disabled = false }) {
  const name = side === 'min'
    ? `items.${index}.minInclusive`
    : `items.${index}.maxInclusive`;

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <label className={cn('gap-2 flex items-center text-sm')}>
          <Checkbox
            checked={Boolean(field.value)}
            disabled={disabled}
            onCheckedChange={(checked) => field.onChange(checked === true)}
          />
          {inclusiveLabel(side)}
        </label>
      )}
    />
  );
}

function PassingValues({
  index,
  control,
  optionRows,
  errors,
  disabled = false,
}) {
  const filled = optionRows.filter((option) => String(option?.value ?? '').trim());
  const fieldError = errors?.items?.[index]?.passingValues?.message;

  return (
    <Controller
      name={`items.${index}.passingValues`}
      control={control}
      render={({ field }) => (
        <div>
          <p className="mb-2 text-sm font-medium">合格值</p>
          {filled.length === 0 ? (
            <p className="text-sm text-muted-foreground">先填寫選項代碼，再勾選合格值。</p>
          ) : (
            <ul className="m-0 list-none p-0">
              {filled.map((option) => {
                const value = String(option.value).trim();
                const selected = (field.value ?? []).includes(value);
                return (
                  <li key={value} className="mb-1">
                    <label className="gap-2 flex items-center text-sm">
                      <Checkbox
                        checked={selected}
                        disabled={disabled}
                        onCheckedChange={(checked) => {
                          const current = field.value ?? [];
                          field.onChange(checked === true
                            ? [...current, value]
                            : current.filter((item) => item !== value));
                        }}
                      />
                      {option.label || value}
                    </label>
                  </li>
                );
              })}
            </ul>
          )}
          {fieldError ? <p className="form_error">{fieldError}</p> : null}
        </div>
      )}
    />
  );
}

InspectionCriteriaFields.propTypes = {
  index: PropTypes.number.isRequired,
  control: PropTypes.object.isRequired,
  register: PropTypes.func.isRequired,
  errors: PropTypes.object,
  readOnly: PropTypes.bool,
};

InclusiveField.propTypes = {
  index: PropTypes.number.isRequired,
  side: PropTypes.oneOf(['min', 'max']).isRequired,
  control: PropTypes.object.isRequired,
  disabled: PropTypes.bool,
};

PassingValues.propTypes = {
  index: PropTypes.number.isRequired,
  control: PropTypes.object.isRequired,
  optionRows: PropTypes.arrayOf(PropTypes.object),
  errors: PropTypes.object,
  disabled: PropTypes.bool,
};

export default InspectionCriteriaFields;
